import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, PencilLine, XCircle } from "lucide-react";
import { actOnApproval, type ApprovalActAction } from "../lib/platform-contracts";

type Choice = Extract<ApprovalActAction, "approve" | "reject" | "needs_revision">;

const OPTIONS: Array<{ id: Choice; label: string; hint: string }> = [
  {
    id: "approve",
    label: "تأیید",
    hint: "این مرحله را تأیید می‌کنم",
  },
  {
    id: "reject",
    label: "عدم تأیید",
    hint: "سند را رد می‌کنم",
  },
  {
    id: "needs_revision",
    label: "نیازمند اصلاح",
    hint: "باید اصلاح شود و دوباره بررسی شود",
  },
];

type Props = {
  open: boolean;
  apiBase: string;
  contractId: string;
  processId: string;
  taskTitle?: string;
  onClose: () => void;
  onSuccess?: () => void;
};

export function ApprovalActSheet({
  open,
  apiBase,
  contractId,
  processId,
  taskTitle,
  onClose,
  onSuccess,
}: Props) {
  const [choice, setChoice] = useState<Choice>("approve");
  const [explanation, setExplanation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setChoice("approve");
    setExplanation("");
    setBusy(false);
    setError(null);
  }, [open, contractId, processId]);

  if (!open) return null;

  const needsExplanation = choice === "reject" || choice === "needs_revision";
  const canSubmit = !busy && (!needsExplanation || explanation.trim().length > 0);

  async function onConfirm() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await actOnApproval(apiBase, contractId, processId, {
        action: choice,
        ...(choice === "reject"
          ? { rejectionReason: explanation.trim() }
          : choice === "needs_revision"
            ? { revisionReason: explanation.trim() }
            : explanation.trim()
              ? { comment: explanation.trim() }
              : {}),
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "ثبت وضعیت ناموفق بود");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sheet-backdrop" role="presentation" onClick={onClose}>
      <div
        className="sheet-card approval-sheet"
        role="dialog"
        aria-label="تعیین وضعیت تأیید"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet-head">
          <h2>تأیید / عدم تأیید</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="بستن">
            ×
          </button>
        </header>
        <div className="consumer-chip-row" style={{ marginBottom: "0.5rem" }}>
          <span className="consumer-chip">تصمیم سریع و روشن</span>
          <span className="consumer-chip">توضیح اختیاری یا الزامی</span>
        </div>
        <p className="settings-hint">
          {taskTitle
            ? `وضعیت بررسی برای «${taskTitle}» را مشخص کنید.`
            : "وضعیت بررسی این مرحله را مشخص کنید."}
        </p>
        <div className="approval-options" role="radiogroup" aria-label="وضعیت تأیید">
          {OPTIONS.map((opt) => {
            const on = choice === opt.id;
            const Icon =
              opt.id === "approve"
                ? CheckCircle2
                : opt.id === "reject"
                  ? XCircle
                  : PencilLine;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={on}
                className={`approval-option${on ? " on" : ""}`}
                disabled={busy}
                onClick={() => setChoice(opt.id)}
              >
                <Icon size={18} />
                <span>
                  <strong>{opt.label}</strong>
                  <small>{opt.hint}</small>
                </span>
              </button>
            );
          })}
        </div>
        <label className="approval-explain-label" htmlFor="approval-explain">
          توضیح{needsExplanation ? " (الزامی)" : " (اختیاری)"}
        </label>
        <textarea
          id="approval-explain"
          rows={3}
          value={explanation}
          disabled={busy}
          onChange={(e) => setExplanation(e.target.value)}
          placeholder={
            needsExplanation
              ? "دلیل رد یا نیازمند اصلاح را بنویسید"
              : "در صورت نیاز توضیح کوتاه بنویسید"
          }
        />
        {error ? <p className="reader-login-error">{error}</p> : null}
        <div className="sheet-actions">
          <button type="button" className="ghost-btn" disabled={busy} onClick={onClose}>
            انصراف
          </button>
          <button
            type="button"
            className="primary-btn"
            disabled={!canSubmit}
            onClick={() => void onConfirm()}
          >
            {busy ? <Loader2 size={16} className="spin" /> : null}
            ثبت وضعیت
          </button>
        </div>
      </div>
    </div>
  );
}
